import { useCallback } from 'react';
import { useGetAccessToken } from '../../client/access-token';
import { apiUrl } from '../../config';
import { useSnackbar } from 'notistack';
import { useT } from '../../i18n';

/**
 * Custom hook for downloading audio files from Kontaktaufnahmen
 * Makes a direct fetch request to properly handle binary blob responses
 * @returns An object containing functions to download audio files.
 */
export const useAudioDownload = () => {
    const getAccessToken = useGetAccessToken();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useT('kontakte');

    const downloadSingleAudio = useCallback(async (kontaktaufnahmeId: string, kontaktVorname?: string | null, kontaktNachname?: string | null) => {
        const accessToken = await getAccessToken();

        if (!accessToken) {
            throw new Error('Not authenticated. Please log in again.');
        }

        const response = await fetch(
            `${apiUrl}/api/v1/kontaktaufnahme/${kontaktaufnahmeId}/audio`,
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            }
        );

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error(`Audio file for ${kontaktaufnahmeId} not found.`);
            }
            throw new Error(`Failed to download audio for ${kontaktaufnahmeId}: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const contentType = response.headers.get('content-type');
        let extension = 'mp3';
        if (contentType?.includes('wav')) {
            extension = 'wav';
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const namePart = [kontaktVorname, kontaktNachname]
            .filter(Boolean)
            .join('_');
        link.download = namePart
            ? `${namePart}_${kontaktaufnahmeId}.${extension}`
            : `audio_${kontaktaufnahmeId}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, [getAccessToken]);

    const downloadAudio = useCallback(async (kontaktaufnahmeId: string | null | undefined, kontaktVorname?: string | null, kontaktNachname?: string | null) => {
        if (!kontaktaufnahmeId) {
            enqueueSnackbar(t('no-audio-available'), { variant: 'warning' });
            return;
        }
        try {
            await downloadSingleAudio(kontaktaufnahmeId, kontaktVorname, kontaktNachname);
        } catch (error) {
            console.error('Error downloading audio:', error);
            enqueueSnackbar((error as Error).message, { variant: 'error' });
        }
    }, [downloadSingleAudio, enqueueSnackbar, t]);

    const downloadMultipleAudio = useCallback(async (ids: string[]) => {
        let successCount = 0;
        let failedCount = 0;
        for (const id of ids) {
            try {
                await downloadSingleAudio(id);
                successCount++;
                // Artificial delay to prevent browser from blocking multiple downloads
                await new Promise(resolve => setTimeout(resolve, 800));
            } catch (error) {
                console.error(`Error downloading audio for ${id}:`, error);
                failedCount++;
            }
        }

        if (successCount > 0 && failedCount === 0) {
            enqueueSnackbar(t('bulk-audio-download-success', { count: successCount }), { variant: 'success' });
        } else if (successCount > 0 && failedCount > 0) {
            enqueueSnackbar(t('bulk-audio-download-partial-success', { successCount, failedCount }), { variant: 'warning' });
        } else if (failedCount > 0) {
            enqueueSnackbar(t('bulk-audio-download-failure', { count: failedCount }), { variant: 'error' });
        }
    }, [downloadSingleAudio, enqueueSnackbar, t]);

    return {
        downloadAudio,
        downloadMultipleAudio
    };
};

